import React, { FC, useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Button, Card, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { CampaignRuleView, RulesModel } from '../../CashbackModel';
import { InfoIcon, LightTooltip, EditIcon, ReorderIcon, DeleteIcon } from '../../../../components';
import { useCommonStyles } from '../../../../styles';
import { useStyles } from './styles';
import { useApiError, useLoading } from '../../../../providers';
import { updateRulesPriority } from '../../CashbackService';

interface Props {
  translation: any;
  rules: CampaignRuleView[];
  onEdit: (ruleId: number) => void;
  onDelete: (ruleId: number) => void;
}

export const RulesTable: FC<Props> = ({ translation, rules, onEdit, onDelete }) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const { addError } = useApiError();
  const { setIsLoading } = useLoading();
  const [localItems, setLocalItems] = useState<CampaignRuleView[]>([]);

  useEffect(() => {
    const items = rules.sort((a, b) => a.priority - b.priority);
    setLocalItems(items);
  }, [rules]);

  const updatePriority = useCallback(
    async (list: CampaignRuleView[]) => {
      try {
        setIsLoading(true);
        const updateModels: RulesModel[] = [];
        list.forEach((item) => {
          updateModels.push({
            ruleId: item.id,
            priority: item.priority,
          });
        });

        await updateRulesPriority({ ruleModels: updateModels });
      } catch (e) {
        addError(JSON.stringify(e.response.message));
      } finally {
        setIsLoading(false);
      }
    },
    [addError, setIsLoading],
  );

  const handleDragEnd = async (result: DropResult) => {
    let updatedList: CampaignRuleView[] = localItems;

    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    setLocalItems((prev: any) => {
      const temp = [...prev];
      if (result.destination) {
        const d = temp[result.destination.index];
        const dPriority = temp[result.destination.index].priority;
        const sPriority = temp[result.source.index].priority;

        temp[result.destination.index] = temp[result.source.index];
        temp[result.destination.index].priority = dPriority;
        temp[result.source.index] = d;
        temp[result.source.index].priority = sPriority;
      }

      updatedList = temp;
      return temp;
    });

    await updatePriority(updatedList);
  };

  return (
    <TableContainer>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow className={commonClasses.tableHeader}>
            <TableCell align="left">&nbsp;</TableCell>
            <TableCell align="left">
              {translation.campaign_priority}
              <LightTooltip title={translation.campaign_priority_tooltip} arrow placement="right-start">
                <IconButton size="small" style={{ marginTop: '-10px' }}>
                  <InfoIcon className={classes.infoIcon} />
                </IconButton>
              </LightTooltip>
            </TableCell>
            <TableCell align="left">{translation.campaign_rule_name}</TableCell>
            <TableCell align="left">{translation.campaign_partnerships}</TableCell>
            <TableCell align="left">{translation.campaign_account_types}</TableCell>
            <TableCell align="left">%</TableCell>
            <TableCell align="left">{translation.camapign_trading_volume}</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        {rules.length === 0 ? (
          <TableRow className={commonClasses.tableRow}>
            <TableCell align="center" className={classes.emptyMessage} colSpan={8}>
              {translation.no_rules}
            </TableCell>
          </TableRow>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(droppableProvided: DroppableProvided) => (
                <TableBody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {localItems.map((item, index: number) => (
                    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                      {(draggableProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          style={{
                            ...draggableProvided.draggableProps.style,
                            background: snapshot.isDragging ? 'rgba(245,245,245, 0.75)' : 'none',
                          }}
                        >
                          <TableCell align="left">
                            <div {...draggableProvided.dragHandleProps}>{!item.isDefault && <ReorderIcon />}</div>
                          </TableCell>
                          <TableCell align="left">{item.isDefault ? '-' : item.priority}</TableCell>
                          <TableCell align="left">{item.name}</TableCell>
                          <TableCell align="left">
                            {item.partnerships.length === 0
                              ? translation.all
                              : item.partnerships.map((partnership) => (
                                  <Card key={partnership} className={classes.partnershipCard}>
                                    {partnership}
                                  </Card>
                                ))}
                          </TableCell>
                          <TableCell align="left">
                            {item.accountTypes.length === 0
                              ? translation.all
                              : item.accountTypes.map((type) => (
                                  <Card key={type} className={classes.partnershipCard}>
                                    {type}
                                  </Card>
                                ))}
                          </TableCell>
                          <TableCell align="left">{Number.isInteger(item.percentage) ? item.percentage : item.percentage.toString().match(/^-?\d+(?:\.\d{0,2})?/)}%</TableCell>
                          <TableCell align="left">
                            {item.minVolume === 0 && item.maxVolume === null
                              ? translation.all_trading_volumes
                              : item.maxVolume === null
                              ? `${item.minVolume} - ∞ ${item.volumeType === 'USD' ? 'USD' : translation.text_696}`
                              : `${item.minVolume} - ${item.maxVolume} ${item.volumeType === 'USD' ? 'USD' : translation.text_696}`}
                          </TableCell>
                          <TableCell align="left">
                            <Button className={classes.editButton} onClick={() => onEdit(item.id)}>
                              <EditIcon />
                            </Button>
                            <Button className={classes.editButton} onClick={() => onDelete(item.id)}>
                              <DeleteIcon />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Table>
    </TableContainer>
  );
};
